/**
 * 奖励分发配置 — 将 uo_config 中的 JSON/比例转为后台可读摘要。
 */

const ASSET_ZH: Record<string, string> = {
  ep1: 'EP1',
  sp1: 'SP1',
  ep2: 'EP2',
  sp2: 'SP2',
  rp: 'RP',
  tp: 'TP',
  fc: 'FC',
  uo: 'UO',
  ap: 'AP',
};

const LEG_ZH: Record<string, string> = {
  credit: '入账',
  fee: '手续费',
  burn: '销毁',
};

function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/** 0.52 → 52%；已为百分数则原样加 % */
export function formatPctRatio(pct: string | number): string {
  const n = typeof pct === 'number' ? pct : Number(pct);
  if (!Number.isFinite(n)) return String(pct);
  const display = n > 0 && n <= 1 ? n * 100 : n;
  const rounded =
    Math.abs(display - Math.round(display)) < 0.05
      ? String(Math.round(display))
      : display.toFixed(1).replace(/\.0$/, '');
  return `${rounded}%`;
}

function formatSplitRows(rows: Array<{ asset?: string; pct?: string; leg?: string }>): string {
  return rows
    .map((r) => {
      const asset = ASSET_ZH[r.asset ?? ''] ?? r.asset ?? '?';
      const p = formatPctRatio(String(r.pct ?? '0'));
      if (r.leg && r.leg !== 'credit') {
        return `${LEG_ZH[r.leg] ?? r.leg} ${asset} ${p}`;
      }
      return `${asset} ${p}`;
    })
    .join(' · ');
}

function formatRecordMap(
  obj: Record<string, string | number>,
  suffix = '',
): string {
  return Object.entries(obj)
    .map(([k, v]) => `${k}${suffix ? ` ${suffix}` : ''} ${v}`)
    .join(' · ');
}

function formatJsonById(id: string, raw: string): { short: string; detail?: string } {
  const parsed = tryParseJson(raw);
  if (parsed === null) {
    return { short: raw.length > 60 ? `${raw.slice(0, 60)}…` : raw };
  }

  switch (id) {
    case 'static_payout_split':
    case 'withdraw_split_static':
    case 'withdraw_split_dynamic':
      if (Array.isArray(parsed)) {
        const short = formatSplitRows(parsed as Array<{ asset?: string; pct?: string; leg?: string }>);
        return { short, detail: raw };
      }
      break;
    case 'static_main_principal':
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const short = Object.entries(parsed as Record<string, string>)
          .map(([pkg, usd]) => `${pkg} $${usd}`)
          .join(' · ');
        return { short, detail: raw };
      }
      break;
    case 'static_main_exit_multiplier':
    case 'static_new_system_register_exit':
    case 'static_legacy_register_exit':
    case 'static_sub_exit_multiplier':
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const short = Object.entries(parsed as Record<string, string>)
          .map(([coin, v]) => `${(ASSET_ZH[coin] ?? coin).toUpperCase()} ×${v}`)
          .join(' · ');
        return { short, detail: raw };
      }
      break;
    case 'dynamic_share_bonus': {
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const o = parsed as Record<string, unknown>;
        const parts: string[] = [];
        if (o.E3_total != null) parts.push(`E3 池 ${o.E3_total} EP`);
        for (const key of ['E1', 'E2'] as const) {
          const caps = o[key];
          if (caps && typeof caps === 'object') {
            const top = Object.entries(caps as Record<string, string>)
              .slice(0, 3)
              .map(([lv, ep]) => `${lv}:${ep}`)
              .join(', ');
            parts.push(`${key} {${top}…}`);
          }
        }
        return { short: parts.join(' · ') || raw.slice(0, 80), detail: raw };
      }
      break;
    }
    default:
      if (Array.isArray(parsed)) {
        const first = parsed[0];
        if (first && typeof first === 'object' && 'pct' in (first as object)) {
          return {
            short: formatSplitRows(parsed as Array<{ asset?: string; pct?: string; leg?: string }>),
            detail: raw,
          };
        }
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const note = (parsed as { note?: string }).note;
        if (note) return { short: note, detail: raw };
        return { short: formatRecordMap(parsed as Record<string, string>), detail: raw };
      }
  }

  const compact = JSON.stringify(parsed);
  return {
    short: compact.length > 72 ? `${compact.slice(0, 72)}…` : compact,
    detail: raw,
  };
}

export function formatRewardDistributionValue(
  id: string,
  valueKind: string,
  effectiveValue: string,
): { short: string; detail?: string } {
  if (!effectiveValue || effectiveValue.startsWith('(')) {
    return { short: effectiveValue || '—' };
  }

  switch (valueKind) {
    case 'percent':
      return {
        short: formatPctRatio(effectiveValue),
        detail: `原始值 ${effectiveValue}`,
      };
    case 'boolean':
      return { short: effectiveValue === 'true' ? '开启' : '关闭' };
    case 'amount':
      return { short: effectiveValue };
    case 'json':
      return formatJsonById(id, effectiveValue);
    default:
      return { short: effectiveValue };
  }
}
